// src/pages/Transactions/PointTransfer.jsx

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { useParams } from 'react-router-dom';

const PointTransfer = () => {
    const { userType = 'super-distributor' } = useParams();
    const { user: currentUser, updateUser } = useAuth();

    const [loading, setLoading] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [users, setUsers] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [userGroups, setUserGroups] = useState({});
    const [currentUserBalance, setCurrentUserBalance] = useState(currentUser?.walletBalance || 0);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });
    
    // ✅ New filter states
    const [filters, setFilters] = useState({
        fromUser: '',
        toUser: '',
        startDate: '',
        endDate: '',
        type: '',
        status: ''
    });
    
    const [formData, setFormData] = useState({
        userId: '',
        type: 'credit',
        amount: '',
        password: ''
    });

    // Get the module title based on userType prop
    const getUserTypeLabel = () => {
        switch(userType) {
            case 'super-distributor':
                return 'Super Distributor';
            case 'distributor':
                return 'Distributor';
            case 'retailer':
                return 'Retailer';
            case 'admin':
                return 'Admin';
            default:
                return 'Point Transfer';
        }
    };

    const getModuleDescription = () => {
        switch(userType) {
            case 'super-distributor':
                return 'Transfer points to your distributors, retailers, and users';
            case 'distributor':
                return 'Transfer points to your retailers and users';
            case 'retailer':
                return 'Transfer points to your customers';
            case 'admin':
                return 'Transfer points to super distributors, distributors, retailers, and users';
            default:
                return 'Manage point transfers';
        }
    };

    const getModuleParam = () => {
        switch(userType) {
            case 'super-distributor':
                return 'sub-admin';
            case 'distributor':
                return 'distributor';
            case 'retailer':
                return 'retailer';
            case 'admin':
                return 'admin';
                case 'user':
                return 'user';
            default:
                return '';
        }
    };

    useEffect(() => {
        fetchDownlineUsers();
        fetchTransactions();
    }, [pagination.page, userType]);

    // ✅ Fetch transactions with filters
    const fetchTransactions = async () => {
        try {
            setLoading(true);
            
            // Build params with filters
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                module: getModuleParam()
            };
            
            // Add filters if they have values
            if (filters.fromUser) params.fromUser = filters.fromUser;
            if (filters.toUser) params.toUser = filters.toUser;
            if (filters.startDate) params.startDate = filters.startDate;
            if (filters.endDate) params.endDate = filters.endDate;
            if (filters.type) params.type = filters.type;
            if (filters.status) params.status = filters.status;
            
            const response = await api.get('/admin/transactions', { params });
            
            setTransactions(response.data.transactions || []);
            setPagination(prev => ({
                ...prev,
                total: response.data.pagination?.total || 0,
                pages: response.data.pagination?.pages || 1
            }));
        } catch (error) {
            toast.error('Failed to fetch transactions');
        } finally {
            setLoading(false);
        }
    };

    // ✅ Handle filter changes
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    // ✅ Apply filters
    const applyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchTransactions();
    };

    // ✅ Clear filters
    const clearFilters = () => {
        setFilters({
            fromUser: '',
            toUser: '',
            startDate: '',
            endDate: '',
            type: '',
            status: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
        setTimeout(fetchTransactions, 100);
    };

    const fetchDownlineUsers = async () => {
        try {
            setLoadingUsers(true);
            const response = await api.get('/admin/downline-users', {
                params: { module: getModuleParam() }
            });
            
            const allUsers = response.data.users || [];
            setUsers(allUsers);
            
            if (response.data.currentUser && response.data.currentUser.walletBalance !== undefined) {
                const newBalance = response.data.currentUser.walletBalance;
                setCurrentUserBalance(newBalance);
                if (updateUser) {
                    updateUser({ walletBalance: newBalance });
                }
            }
            
            const groups = {};
            allUsers.forEach(user => {
                const role = user.role;
                if (!groups[role]) {
                    groups[role] = [];
                }
                groups[role].push(user);
            });
            
            setUserGroups(groups);
        } catch (error) {
            console.error('Error fetching downline users:', error);
            toast.error('Failed to fetch users in your downline');
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.userId) {
            toast.error('Please select a user');
            return;
        }
        
        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            toast.error('Please enter a valid amount');
            return;
        }
        
        if (!formData.password) {
            toast.error('Please enter your password');
            return;
        }
        
        setLoading(true);
        try {
            const response = await api.post('/admin/transactions/transfer', {
                userId: formData.userId,
                type: formData.type,
                amount: parseFloat(formData.amount),
                adminPassword: formData.password,
                module: getModuleParam()
            });
            
            toast.success('Transaction completed successfully');
            
            const updatedUserIndex = users.findIndex(u => u._id === formData.userId);
            if (updatedUserIndex !== -1 && response.data.transaction) {
                const updatedUsers = [...users];
                const transaction = response.data.transaction;
                if (formData.type === 'credit') {
                    updatedUsers[updatedUserIndex].walletBalance = transaction.walletBalanceAfter;
                } else if (formData.type === 'debit') {
                    updatedUsers[updatedUserIndex].walletBalance = transaction.walletBalanceAfter;
                }
                setUsers(updatedUsers);
            }
            
            if (response.data.currentUserBalance !== undefined) {
                setCurrentUserBalance(response.data.currentUserBalance);
                if (updateUser) {
                    updateUser({ walletBalance: response.data.currentUserBalance });
                }
            } else if (response.data.transaction && response.data.transaction.fromWalletBalanceAfter !== undefined) {
                setCurrentUserBalance(response.data.transaction.fromWalletBalanceAfter);
                if (updateUser) {
                    updateUser({ walletBalance: response.data.transaction.fromWalletBalanceAfter });
                }
            }
            
            setFormData({
                userId: '',
                type: 'credit',
                amount: '',
                password: ''
            });
            
            fetchTransactions();
            fetchDownlineUsers();
            
        } catch (error) {
            toast.error(error.response?.data?.error || 'Transaction failed');
        } finally {
            setLoading(false);
        }
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getSelectedUser = () => {
        return users.find(u => u._id === formData.userId);
    };

    const getRoleDisplay = (role) => {
        if (!role) return '';
        const roles = {
            'admin': 'Admin',
            'sub-admin': 'Super Distributor',
            'distributor': 'Distributor',
            'retailer': 'Retailer',
            'user': 'User'
        };
        return roles[role] || role;
    };
    const getTransactionDirection = (transaction) => {
        // New unified writes use type: 'TRANSFER' + metadata.transferType.
        // Legacy dual-entry records still carry type: 'CREDIT' / 'DEBIT' directly.
        if (transaction.type === 'CREDIT') return 'CREDIT';
        if (transaction.type === 'DEBIT') return 'DEBIT';
        if (transaction.type === 'TRANSFER') {
            if (transaction.metadata?.transferType === 'admin_to_user') return 'CREDIT';
            if (transaction.metadata?.transferType === 'user_to_admin') return 'DEBIT';
        }
        return transaction.type;
    };

    const formatPartyDisplay = (party) => {
        if (!party?.username) return 'System';
        const roleTag = party.role && party.role !== 'user' ? ` (${getRoleDisplay(party.role)})` : '';
        return `${party.username}${roleTag}`;
    };
    const getRoleIcon = (role) => {
        const icons = {
            'sub-admin': '👑',
            'distributor': '📦',
            'retailer': '🏪',
            'user': '👤',
            'admin': '⚡'
        };
        return icons[role] || '👤';
    };

    const getActionText = () => {
        switch(userType) {
            case 'admin':
                return 'Transfer to Super Distributors, Distributors, Retailers & Users';
            case 'super-distributor':
                return 'Transfer to Distributors, Retailers & Users';
            case 'distributor':
                return 'Transfer to Retailers & Users';
            case 'retailer':
                return 'Transfer to Users';
            default:
                return 'Submit Transaction';
        }
    };

    // ✅ Get transaction type options
    const getTransactionTypes = () => {
        const types = [
            { value: '', label: 'All Types' },
            { value: 'CREDIT', label: 'Credit' },
            { value: 'DEBIT', label: 'Debit' },
            { value: 'TRANSFER', label: 'Transfer' },
            { value: 'BET', label: 'Bet' },
            { value: 'WIN', label: 'Win' },
            { value: 'COMMISSION', label: 'Commission' },
            { value: 'DEPOSIT', label: 'Deposit' },
            { value: 'WITHDRAWAL', label: 'Withdrawal' }
        ];
        return types;
    };

    // ✅ Get status options
    const getStatusOptions = () => {
        return [
            { value: '', label: 'All Status' },
            { value: 'COMPLETED', label: 'Completed' },
            { value: 'PENDING', label: 'Pending' },
            { value: 'FAILED', label: 'Failed' },
            { value: 'CANCELLED', label: 'Cancelled' },
            { value: 'PROCESSING', label: 'Processing' }
        ];
    };

    return (
        <div className="point-transfer-page">
            <div className="container-fluid">
                <div className="page-hedding-header">
                    <h1 className="page-heading h3 text-uppercase fs-4">
                        {getUserTypeLabel()} 
                    </h1>
                    
                    <div className="alert alert-info mt-3" style={{ background: '#300730', border: '1px solid #fff7ca' }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <strong>Your Balance:</strong>
                            </div>
                            <div>
                                <span className="h5 mb-0 text-success">
                                    {formatAmount(currentUserBalance)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Balance Management Form */}
                <div className="admin-card-one">
                    <div className="admin-card-header">
                        <h2 className="admin-card-heading">Balance Management</h2>
                    </div>
                    <div className="admin-card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-4">
                                <div className="col-12">
                                    <label className="admin-label-one">
                                        Select User
                                        {loadingUsers && <span className="text-muted ms-2">(Loading...)</span>}
                                    </label>
                                    
                                    {!loadingUsers && users.length === 0 && (
                                        <div className="alert alert-info mt-2">
                                            No users found in your downline. You can only transfer points to users under you.
                                        </div>
                                    )}
                                    
                                    <select
                                        name="userId"
                                        value={formData.userId}
                                        onChange={handleChange}
                                        className="form-select admin-select-one mt-2"
                                    >
                                        <option value="">Select user</option>
                                        {users.map(user => (
                                            <option key={user._id} value={user._id}>
                                                {user.username}
                                                {user.fullName ? ` (${user.fullName})` : ''}
                                                {user.hierarchyInfo ? ` - ${user.hierarchyInfo}` : ''}
                                                {' - '}Balance: ₹{user.walletBalance ?? 0}
                                            </option>
                                        ))}
                                    </select>
                                    
                                    {getSelectedUser() && (
                                        <div className="mt-3 p-3 rounded" style={{ background: '#300730', border: '1px solid #fff7ca' }}>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <small className="text-muted">Username:</small>
                                                    <div><strong>{getSelectedUser().username}</strong></div>
                                                </div>
                                                <div className="col-md-6">
                                                    <small className="text-muted">Current Balance:</small>
                                                    <div><strong className="text-success">₹{getSelectedUser().walletBalance?.toFixed(2) || '0.00'}</strong></div>
                                                </div>
                                                <div className="col-md-6 mt-2">
                                                    <small className="text-muted">Full Name:</small>
                                                    <div>{getSelectedUser().fullName || '-'}</div>
                                                </div>
                                                <div className="col-md-6 mt-2">
                                                    <small className="text-muted">Role:</small>
                                                    <div>
                                                        <span className="badge" style={{ background: '#9c27b0' }}>
                                                            {getRoleIcon(getSelectedUser().role)} {getRoleDisplay(getSelectedUser().role)}
                                                        </span>
                                                        {getSelectedUser().hierarchyInfo && (
                                                            <small className="text-muted ms-2">({getSelectedUser().hierarchyInfo})</small>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="col-12">
                                    <label className="admin-label-one">Transaction Type</label>
                                    <div className="admin-check-one">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="type"
                                                id="Credit"
                                                value="credit"
                                                checked={formData.type === 'credit'}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="Credit">
                                                Credit (Add Balance)
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="type"
                                                id="Debit"
                                                value="debit"
                                                checked={formData.type === 'debit'}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="Debit">
                                                Debit (Deduct Balance)
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <label className="admin-label-one">Points (₹)</label>
                                    <input
                                        type="number"
                                        name="amount"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        className="form-control admin-input-one"
                                        placeholder="Enter Points"
                                        step="0.01"
                                        min="0"
                                        required
                                    />
                                </div>

                                <div className="col-12">
                                    <label className="admin-label-one">Admin Password</label>
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="form-control admin-input-one"
                                        placeholder="Enter your password to confirm"
                                        required
                                    />
                                </div>

                                <div className="col-12">
                                    <button type="submit" className="admin-submit-one" disabled={loading}>
                                        {loading ? 'Processing...' : getActionText()}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Transaction History with Filters */}
                <div className="admin-card-one">
                    <div className="admin-card-header">
                        <h2 className="admin-card-heading">Transaction History</h2>
                    </div>
                    <div className="admin-card-body">
                        {/* ✅ Filter Section */}
                        <div className="filter-section mb-4">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="admin-label-one">Parent ID</label>
                                    <input
                                        type="text"
                                        name="fromUser"
                                        value={filters.fromUser}
                                        onChange={handleFilterChange}
                                        className="form-control admin-input-one"
                                        placeholder="Search by username"
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="admin-label-one">Child ID</label>
                                    <input
                                        type="text"
                                        name="toUser"
                                        value={filters.toUser}
                                        onChange={handleFilterChange}
                                        className="form-control admin-input-one"
                                        placeholder="Search by username"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="admin-label-one">From Date</label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={filters.startDate}
                                        onChange={handleFilterChange}
                                        className="form-control admin-input-one"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <label className="admin-label-one">To Date</label>
                                    <input
                                        type="date"
                                        name="endDate"
                                        value={filters.endDate}
                                        onChange={handleFilterChange}
                                        className="form-control admin-input-one"
                                    />
                                </div>
                                {/* <div className="col-md-2">
                                    <label className="admin-label-one">Type</label>
                                    <select
                                        name="type"
                                        value={filters.type}
                                        onChange={handleFilterChange}
                                        className="form-select admin-select-one"
                                    >
                                        {getTransactionTypes().map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div> */}
                                {/* <div className="col-md-2">
                                    <label className="admin-label-one">Status</label>
                                    <select
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                        className="form-select admin-select-one"
                                    >
                                        {getStatusOptions().map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                </div> */}
                                <div className="col-12 d-flex gap-2">
                                    <button 
                                        onClick={applyFilters} 
                                        className="admin-submit-one"
                                        disabled={loading}
                                    >
                                        <i className="fas fa-search me-2"></i>
                                        Apply Filters
                                    </button>
                                    <button 
                                        onClick={clearFilters} 
                                        className="btn btn-secondary"
                                        style={{ background: '#6c757d', border: 'none', padding: '10px 25px' }}
                                    >
                                        <i className="fas fa-undo me-2"></i>
                                        Clear Filters
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <table className="table table-admin-one">
                                <thead>
                                    <tr>
                                        <th>From</th>
                                        <th>To</th>
                                        <th>Type</th>
                                        <th>Points</th>
                                        <th>Balance Before</th>
                                        <th>Balance After</th>
                                        <th>Date & Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : transactions.length > 0 ? (
                                        transactions.map((transaction, index) => {
                                            const direction = getTransactionDirection(transaction);
                                            const isCreditLike = direction === 'CREDIT';
                                            const isDebitLike = direction === 'DEBIT';

                                            const fromDisplay = formatPartyDisplay(transaction.from);
                                            const toDisplay = formatPartyDisplay(transaction.to);

                                            return (
                                                <tr key={transaction._id || index}>
                                                    <td>{fromDisplay}</td>
                                                    <td>{toDisplay}</td>
                                                    <td>
                                                        <span className={`badge ${
                                                            isCreditLike ? 'badge-success' : isDebitLike ? 'badge-warning' : 'badge-secondary'
                                                        }`}>
                                                            {transaction.typeDisplay || (isCreditLike ? 'Credit' : isDebitLike ? 'Debit' : transaction.type)}
                                                        </span>
                                                    </td>
                                                    <td className={isCreditLike ? 'text-success' : isDebitLike ? 'text-danger' : ''}>
                                                        {isCreditLike ? '+' : isDebitLike ? '-' : ''} {formatAmount(transaction.amount)}
                                                    </td>
                                                    <td>{formatAmount(transaction.walletBalanceBefore || 0)}</td>
                                                    <td>{formatAmount(transaction.walletBalanceAfter || 0)}</td>
                                                    <td>{formatDate(transaction.createdAt)}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">
                                                No transactions found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div className="admin-table-bottom">
                                <div className="table-info">
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                                    {pagination.total} entries
                                </div>
                                <div className="table-pagination">
                                    <button
                                        className="page-btn prev"
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        disabled={pagination.page === 1}
                                    >
                                        Prev
                                    </button>
                                    {[...Array(Math.min(pagination.pages, 5))].map((_, i) => {
                                        let pageNum;
                                        if (pagination.pages <= 5) {
                                            pageNum = i + 1;
                                        } else if (pagination.page <= 3) {
                                            pageNum = i + 1;
                                        } else if (pagination.page >= pagination.pages - 2) {
                                            pageNum = pagination.pages - 4 + i;
                                        } else {
                                            pageNum = pagination.page - 2 + i;
                                        }
                                        return (
                                            <button
                                                key={pageNum}
                                                className={`page-btn ${pagination.page === pageNum ? 'active' : ''}`}
                                                onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                    <button
                                        className="page-btn next"
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        disabled={pagination.page === pagination.pages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PointTransfer;   